import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, TriangleAlert } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { FieldLabel } from '@/components/FieldLabel'
import { CodeEditor } from '@/components/CodeEditor'
import { copyToClipboard } from '@/utils/clipboard'
import {
  LIMITED_FORMATS,
  TAG_FORMATS,
  clickTracker,
  creativeTagCodes,
  creativeVastTag,
  emailTag,
  generateTargetId,
  impressionTracker,
  type TagContext,
  type TagCreative,
  type TagFormat,
} from '@/services/creatives/creativeTags'
import { vastVersions } from '@/data/adTagTemplates'

/**
 * Sentinel for the VAST entry in the tag type list. VAST is a URL for a video
 * player rather than one of the ad server tag formats, and it is only offered
 * on a creative that carries a video asset.
 */
const VAST_TAG_TYPE = 'VAST'
type TagTypeId = TagFormat | typeof VAST_TAG_TYPE

interface CreativeTagsPanelProps {
  creative: TagCreative
  /** The line item the tags request through; without one no tag can be built. */
  lineItemId: string
  networkTagId: string
  /** First URL component value, for the email tag's click-through. */
  destinationUrl?: string
  /** Whether the creative has an image asset, which the email tag needs. */
  hasImage: boolean
  /** Whether the creative has a video asset, which the VAST tag needs. */
  hasVideo: boolean
}

/** A read-only, syntax-highlighted code block with its own copy button. */
function TagCode({
  title,
  code,
  note,
  minHeight = '120px',
}: {
  title: string
  code: string
  note?: string
  minHeight?: string
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{title}</Label>
        <Button type="button" variant="ghost" size="sm" onClick={() => copyToClipboard(code)}>
          <Copy className="size-3.5" />
          {t('common.copyToClipboard')}
        </Button>
      </div>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
      {/* A display block, not an editor: nothing to write back, and native
          text selection so the tag can be dragged over and copied. */}
      <CodeEditor
        value={code}
        onChange={() => {}}
        language="html"
        variables="none"
        display
        minHeight={minHeight}
        maxHeight="320px"
      />
    </div>
  )
}

/**
 * The creative's tags for third-party systems: impression and click trackers,
 * the creative tag in each ad server's format, and the tag for emails.
 */
export function CreativeTagsPanel({
  creative,
  lineItemId,
  networkTagId,
  destinationUrl,
  hasImage,
  hasVideo,
}: CreativeTagsPanelProps) {
  const { t } = useTranslation()
  const [format, setFormat] = useState<TagTypeId>('STANDARD_JAVASCRIPT')
  const [vastVersion, setVastVersion] = useState('vast4')
  const [useCookies, setUseCookies] = useState(true)
  // Stable for the life of the panel, so the tag does not change as you read it.
  const [targetId] = useState(generateTargetId)

  const context = useMemo<TagContext>(
    () => ({ parentType: 'lineItemId', parentId: lineItemId, networkTagId, useCookies }),
    [lineItemId, networkTagId, useCookies]
  )

  const isVast = format === VAST_TAG_TYPE

  const tagTypes = useMemo(
    () => [
      ...TAG_FORMATS.map((f) => ({ id: f.id as TagTypeId, label: f.label })),
      ...(hasVideo ? [{ id: VAST_TAG_TYPE as TagTypeId, label: 'VAST' }] : []),
    ],
    [hasVideo]
  )

  const codes = useMemo(
    () =>
      isVast
        ? [
            {
              id: 'vast',
              titleKey: VAST_TAG_TYPE,
              code: creativeVastTag(context, creative, vastVersion),
            },
          ]
        : creativeTagCodes(format as TagFormat, context, creative, targetId),
    [isVast, format, context, creative, targetId, vastVersion]
  )

  // A creative only serves through its line item, so there is nothing to build without one.
  if (!lineItemId) {
    return (
      <Alert>
        <TriangleAlert className="size-4" />
        <AlertDescription>{t('creativeTags.needsLineItem')}</AlertDescription>
      </Alert>
    )
  }

  const emailReason = !destinationUrl
    ? t('creativeTags.emailNeedsUrl')
    : !hasImage
      ? t('creativeTags.emailNeedsImage')
      : null

  const cookieToggle = (
    <div className="flex items-center gap-3">
      <Switch id="creative-tag-cookies" checked={useCookies} onCheckedChange={setUseCookies} />
      <Label htmlFor="creative-tag-cookies" className="flex-col items-start gap-0.5">
        <span>{t('creativeTags.useCookies')}</span>
        <span className="text-xs font-normal text-muted-foreground">
          {useCookies ? t('creativeTags.useCookiesOn') : t('creativeTags.useCookiesOff')}
        </span>
      </Label>
    </div>
  )

  return (
    <Tabs defaultValue="trackers" className="gap-4">
      <TabsList>
        <TabsTrigger value="trackers">{t('creativeTags.trackers')}</TabsTrigger>
        <TabsTrigger value="tag">{t('creativeTags.creativeTag')}</TabsTrigger>
        <TabsTrigger value="email">{t('creativeTags.emailTag')}</TabsTrigger>
      </TabsList>

      <TabsContent value="trackers" className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">{t('creativeTags.trackersDescription')}</p>
        <Alert>
          <TriangleAlert className="size-4" />
          <AlertDescription>{t('creativeTags.trackersDeliverable')}</AlertDescription>
        </Alert>
        {cookieToggle}
        <TagCode
          title={t('creativeTags.impressionTracker')}
          code={impressionTracker(context, creative)}
          minHeight="72px"
        />
        <TagCode
          title={t('creativeTags.clickTracker')}
          code={clickTracker(context, creative)}
          minHeight="72px"
        />
      </TabsContent>

      <TabsContent value="tag" className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">{t('creativeTags.creativeTagDescription')}</p>
        <div className="flex flex-col gap-1">
          <FieldLabel>{t('creativeTags.tagType')}</FieldLabel>
          <Select value={format} onValueChange={(v) => setFormat(v as TagTypeId)}>
            <SelectTrigger className="max-w-80">
              <SelectValue>{tagTypes.find((f) => f.id === format)?.label}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {tagTypes.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t(`creativeTags.formats.${format}`)}
          </p>
        </div>
        {isVast ? (
          <div className="flex flex-col gap-1">
            <FieldLabel>{t('creativeTags.vastVersion')}</FieldLabel>
            <Select value={vastVersion} onValueChange={(v) => setVastVersion(v)}>
              <SelectTrigger className="max-w-80">
                <SelectValue>{vastVersions.find((v) => v.id === vastVersion)?.label}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {vastVersions.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          cookieToggle
        )}
        {codes.map((entry) => (
          <TagCode
            key={entry.id}
            title={t(`creativeTags.codes.${entry.titleKey}`)}
            code={entry.code}
            note={
              LIMITED_FORMATS.includes(format as TagFormat)
                ? t('creativeTags.limitedNote')
                : undefined
            }
          />
        ))}
      </TabsContent>

      <TabsContent value="email" className="flex flex-col gap-4">
        {emailReason ? (
          <Alert variant="destructive">
            <TriangleAlert className="size-4" />
            <AlertDescription>{emailReason}</AlertDescription>
          </Alert>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{t('creativeTags.emailDescription')}</p>
            <Alert>
              <TriangleAlert className="size-4" />
              <AlertDescription>{t('creativeTags.emailNoTargeting')}</AlertDescription>
            </Alert>
            {cookieToggle}
            <TagCode
              title={t('creativeTags.emailCode')}
              code={emailTag(context, creative, destinationUrl!)}
            />
          </>
        )}
      </TabsContent>
    </Tabs>
  )
}
